<script>
/**
 * @component SettingsDashboard
 * @description Panel de configuración con grid de cards por grupo.
 *              Al hacer click en una card muestra el componente correspondiente
 *              con botón para volver a la grilla. Cards que navegan a otra ruta
 *              usan router-link.
 * @example <SettingsDashboard />
 */
import Spinner from "@/components/ui/Spinner";
import InstitutionList from "@/components/settings/institution/InstitutionList";
import MedicalSpecialtyList from "@/components/settings/medicalSpecialty/MedicalSpecialtyList";
import LabelList from "@/components/settings/label/LabelList";
import CommissionList from "./commission/CommissionList";
import MedicalInsuranceList from "@/components/settings/medicalInsurance/MedicalInsuranceList";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import ProgramList from "@/components/settings/program/ProgramList.vue";
import PracticeCatalogList from "@/components/settings/practiceCatalog/PracticeCatalogList.vue";
import ChronicConditionList from "@/components/settings/chronicCondition/ChronicConditionList.vue";
import VademecumList from "@/components/settings/vademecum/VademecumList.vue";
import ProviderNetworkList from "@/components/providers/ProviderNetworkList.vue";
import RolePermissions from "@/components/settings/RolePermissions.vue";
import LiquidationToleranceConfig from "@/components/settings/liquidation/LiquidationToleranceConfig.vue";
import AIConfig from "@/components/settings/ai/AIConfig.vue";
import WhatsappConfig from "@/components/settings/whatsapp/WhatsappConfig.vue";
import WhatsappAuditPanel from "@/components/settings/whatsapp/WhatsappAuditPanel.vue";
// UC-REG-288: pendientes de codificar + auditoría del esquema SNOMED CT.
import SnomedCodingPanel from "@/components/settings/snomed/SnomedCodingPanel.vue";
import WhatsappNotificationsConfig from "@/components/settings/whatsapp-notifications/WhatsappNotificationsConfig.vue";
import PrescriptionLogPanel from "@/components/settings/prescription-log/PrescriptionLogPanel.vue";
import LabCatalog from "@/components/settings/labCatalog/LabCatalog.vue";
import ImageCatalog from "@/components/settings/imageCatalog/ImageCatalog.vue";
import MemberPaymentRulesConfig from "@/components/settings/memberPaymentRules/MemberPaymentRulesConfig.vue";
import { isProgramsEnabled } from "@/services/config";

const SECTIONS = [
  {
    group: "Consultorio Quantux",
    cards: [
      {
        key: "locations",
        label: "Instituciones",
        desc: "Sedes y sucursales",
        icon: "institution",
        color: "navy",
      },
      {
        key: "medical-specialty",
        label: "Especialidades",
        desc: "Especialidades médicas",
        icon: "stethoscope",
        color: "teal",
      },
      {
        key: "labels",
        label: "Etiquetas",
        desc: "Categorías y etiquetas",
        icon: "label",
        color: "purple",
      },
      {
        key: "medical-insurance",
        label: "Seguros médicos",
        desc: "Coberturas externas",
        icon: "check",
        color: "green",
      },
      {
        key: "diagnosis-catalog",
        label: "Diagnósticos",
        desc: "Catálogo de diagnósticos CIE-10",
        icon: "medical_information",
        color: "teal",
        route: "diagnosis-catalog",
      },
      {
        key: "lab-catalog",
        label: "Laboratorio",
        desc: "Determinaciones y rutinas",
        icon: "assignment",
        color: "teal",
      },
      {
        key: "image-catalog",
        label: "Imágenes",
        desc: "Catálogo de estudios de imagen",
        icon: "image",
        color: "navy",
      },
    ],
  },
  {
    group: "Programas Financiador",
    cards: [
      {
        key: "programs",
        label: "Programas de salud",
        desc: "Gestión de programas de salud",
        icon: "pending_actions",
        color: "danger",
        needsPrograms: true,
      },
      {
        key: "protocols",
        label: "Protocolos seguimiento",
        desc: "Protocolos de seguimiento clínico",
        icon: "assignment",
        color: "navy",
        route: "treatment",
      },
    ],
  },
  {
    group: "Maestros Obra Social",
    cards: [
      {
        key: "practice-catalog",
        label: "Prestaciones",
        desc: "Nomenclador de prácticas",
        icon: "assignment",
        color: "navy",
      },
      {
        key: "chronic-conditions",
        label: "Cronicidades",
        desc: "Condiciones crónicas",
        icon: "pending_actions",
        color: "danger",
      },
      {
        key: "vademecum",
        label: "Vademécum",
        desc: "Maestro de medicamentos",
        icon: "medication",
        color: "teal",
      },
    ],
  },
  {
    group: "Planes y Cartilla",
    cards: [
      {
        key: "plans",
        label: "Planes",
        desc: "Planes de cobertura",
        icon: "description",
        color: "purple",
        route: "plans",
      },
      {
        key: "providers-network",
        label: "Red de prestadores",
        desc: "Cartilla de prestadores",
        icon: "institution",
        color: "navy",
      },
    ],
  },
  {
    group: "Cuenta Médica",
    cards: [
      {
        key: "liquidationTolerance",
        label: "Tolerancia de liquidaciones",
        desc: "Margen de diferencia aceptable entre importe esperado y facturado",
        icon: "tune",
        color: "teal",
      },
    ],
  },
  {
    group: "Administración",
    cards: [
      {
        key: "member-payment-rules",
        label: "Reglas de afiliación",
        desc: "Rangos de mora y suspensión automática de afiliados",
        icon: "rule",
        color: "navy",
      },
      {
        key: "role-permissions",
        label: "Roles y permisos",
        desc: "Gestionar permisos por tipo de perfil",
        icon: "admin_panel_settings",
        color: "navy",
      },
      {
        key: "ai-config",
        label: "Asistencia IA",
        desc: "Configuración del módulo de IA clínica",
        icon: "psychology",
        color: "teal",
      },
      {
        key: "whatsapp-config",
        label: "WhatsApp + IA",
        desc: "Credenciales 2chat, prompts del LLM y templates de mensajes",
        icon: "chat",
        color: "green",
      },
      {
        key: "whatsapp-audit",
        label: "Auditoría WhatsApp",
        desc: "Revisión de respuestas interpretadas por el LLM",
        icon: "fact_check",
        color: "purple",
      },
      {
        // UC-REG-288 — Solo útil si el tenant tiene SNOMED_CODING_ENABLED y la
        // org está en modo snomed-*; el panel lo informa si no.
        key: "snomed-coding",
        label: "Codificación SNOMED CT",
        desc: "Pendientes de codificar, auditoría y catálogo de estudios de imagen",
        icon: "radiology",
        color: "teal",
      },
      {
        key: "whatsapp-notifications-config",
        label: "Notificaciones WhatsApp",
        desc: "Envío de recetas, órdenes médicas y turnos por WhatsApp",
        icon: "send",
        color: "green",
      },
      {
        key: "prescription-log",
        label: "Log de recetas",
        desc: "Auditoría de recetas enviadas a web services externos",
        icon: "medication",
        color: "teal",
      },
    ],
  },
];

export default {
  name: "SettingsDashboard",

  components: {
    Spinner,
    InstitutionList,
    LabelList,
    MedicalSpecialtyList,
    CommissionList,
    MedicalInsuranceList,
    ProgramList,
    PracticeCatalogList,
    ChronicConditionList,
    VademecumList,
    ProviderNetworkList,
    RolePermissions,
    LiquidationToleranceConfig,
    AIConfig,
    WhatsappConfig,
    WhatsappAuditPanel,
    SnomedCodingPanel,
    WhatsappNotificationsConfig,
    PrescriptionLogPanel,
    LabCatalog,
    ImageCatalog,
    MemberPaymentRulesConfig,
    BaseSubNav,
  },

  data() {
    return {
      activeSection: null,
      isProgramsEnabled,
    };
  },

  watch: {
    "$route.query.group": {
      immediate: true,
      handler(val) {
        if (val === "maestros-os") {
          this.activeSection = null;
        }
      },
    },
    /**
     * @description Sincroniza `activeSection` con `?section=` en la URL.
     *              Permite que F5 mantenga la sección activa en lugar de
     *              volver a la grilla principal de Configuración.
     *              `replace` (no `push`) para no contaminar el history stack.
     */
    activeSection: {
      handler(val) {
        const current = this.$route.query.section || null;
        if (val === current) return;
        const query = { ...this.$route.query };
        if (val) query.section = val;
        else delete query.section;
        this.$router.replace({ query }).catch(() => {});
      },
    },
    /**
     * @description Lectura inicial: hidrata `activeSection` desde el query
     *              param al cargar el componente (incluye F5 y deep links).
     */
    "$route.query.section": {
      immediate: true,
      handler(val) {
        if (val && val !== this.activeSection) {
          this.activeSection = val;
        }
      },
    },
  },

  computed: {
    /**
     * @description Secciones filtradas por feature flags
     * @returns {Array}
     */
    filteredSections() {
      return SECTIONS.map((s) => ({
        ...s,
        cards: s.cards.filter((c) => {
          if (c.needsPrograms) return this.isProgramsEnabled;
          return true;
        }),
      })).filter((s) => s.cards.length > 0);
    },

    /**
     * @description Indica si se está mostrando solo el grupo Maestros Obra Social
     * @returns {boolean}
     */
    isMaestrosView() {
      return this.$route.query.group === "maestros-os";
    },

    /**
     * @description Secciones a mostrar en la grilla (filtradas si viene de maestros-os)
     * @returns {Array}
     */
    displaySections() {
      if (this.isMaestrosView) {
        return this.filteredSections.filter(
          (s) => s.group === "Maestros Obra Social"
        );
      }
      return this.filteredSections;
    },

    /**
     * @description Label de la sección activa
     * @returns {string}
     */
    activeSectionLabel() {
      if (!this.activeSection) return "";
      for (const section of SECTIONS) {
        const card = section.cards.find((c) => c.key === this.activeSection);
        if (card) return card.label;
      }
      return "";
    },

    /**
     * @description Indica si la sección activa tiene un título global
     *              propio en el header (mapeado en `SETTINGS_SECTION_TITLES`
     *              de Main.vue). Cuando es true, este componente NO pinta
     *              su propio `__section-title` para no duplicarlo con el
     *              header global.
     *
     *              Hardcoded acá para evitar import circular con Main.vue;
     *              mantener sincronizado con `SETTINGS_SECTION_TITLES`
     *              de `src/components/Main.vue`.
     * @returns {boolean}
     */
    sectionHasGlobalTitle() {
      const sectionsWithGlobalTitle = new Set([
        "whatsapp-audit",
        "whatsapp-config",
        "whatsapp-notifications-config",
        "ai-config",
        "prescription-log",
      ]);
      return sectionsWithGlobalTitle.has(this.activeSection);
    },
  },

  methods: {
    /**
     * @description Abre una sección interna (componente inline)
     * @param {Object} card - Card seleccionada
     */
    openSection(card) {
      if (card.route) return;
      this.activeSection = card.key;
    },

    /**
     * @description Vuelve a la grilla de cards
     */
    goBack() {
      this.activeSection = null;
      if (this.$route.query.group) {
        this.$router.replace({
          name: "settings",
          query: { group: this.$route.query.group },
        });
      }
    },
  },
};
</script>

<template lang="pug">
section.settings-page
  //- Vista: sección activa (componente inline)
  template(v-if="activeSection")
    .settings-page__back(v-if="activeSection !== 'practice-catalog'" @click="goBack")
      micon(name="arrow_back")
      span Volver a Configuración
    //- Solo pintamos el section title local cuando la sección NO tiene un
    //- título global propio (ver `sectionHasGlobalTitle`). Si lo tiene, ya
    //- aparece en el header del Main y duplicarlo acá es ruido visual.
    .settings-page__section-title(v-if="activeSection !== 'practice-catalog' && !sectionHasGlobalTitle") {{ activeSectionLabel }}
    .settings-page__content
      InstitutionList(v-if="activeSection === 'locations'")
      MedicalSpecialtyList(v-if="activeSection === 'medical-specialty'")
      LabelList(v-if="activeSection === 'labels'")
      CommissionList(v-if="activeSection === 'commissions'")
      MedicalInsuranceList(v-if="activeSection === 'medical-insurance'")
      ProgramList(v-if="isProgramsEnabled && activeSection === 'programs'")
      PracticeCatalogList(v-if="activeSection === 'practice-catalog'")
      ChronicConditionList(v-if="activeSection === 'chronic-conditions'")
      VademecumList(v-if="activeSection === 'vademecum'")
      ProviderNetworkList(v-if="activeSection === 'providers-network'")
      MemberPaymentRulesConfig(v-if="activeSection === 'member-payment-rules'")
      RolePermissions(v-if="activeSection === 'role-permissions'")
      LiquidationToleranceConfig(v-if="activeSection === 'liquidationTolerance'")
      AIConfig(v-if="activeSection === 'ai-config'")
      WhatsappConfig(v-if="activeSection === 'whatsapp-config'")
      WhatsappAuditPanel(v-if="activeSection === 'whatsapp-audit'")
      SnomedCodingPanel(v-if="activeSection === 'snomed-coding'")
      WhatsappNotificationsConfig(v-if="activeSection === 'whatsapp-notifications-config'")
      PrescriptionLogPanel(v-if="activeSection === 'prescription-log'")
      LabCatalog(v-if="activeSection === 'lab-catalog'")
      ImageCatalog(v-if="activeSection === 'image-catalog'")

  //- Vista: grilla de cards
  template(v-else)
    .settings-page__back(v-if="isMaestrosView" @click="$router.push({ name: 'settings' })")
      micon(name="arrow_back")
      span Volver a Configuración
    .settings-page__section-title(v-if="isMaestrosView") Maestros Obra Social
    .settings-page__groups
      .settings-group(v-for="section in displaySections" :key="section.group")
        .settings-group__label {{ section.group }}
        .settings-group__body
          .settings-group__grid
            template(v-for="card in section.cards")
              router-link.settings-card(
                v-if="card.route"
                :key="card.key"
                :to="{ name: card.route }"
              )
                .settings-card__icon(:class="'settings-card__icon--' + card.color")
                  micon(:name="card.icon")
                .settings-card__body
                  .settings-card__label {{ card.label }}
                  .settings-card__desc {{ card.desc }}
              .settings-card(
                v-else
                :key="card.key"
                @click="openSection(card)"
              )
                .settings-card__icon(:class="'settings-card__icon--' + card.color")
                  micon(:name="card.icon")
                .settings-card__body
                  .settings-card__label {{ card.label }}
                  .settings-card__desc {{ card.desc }}
</template>

<style lang="scss" scoped>
.settings-page {
  &__back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: $text-secondary-new;
    font-size: 14px;
    cursor: pointer;
    margin-bottom: 16px;
    padding: 6px 0;

    .micon {
      fill: $text-secondary-new;
      width: 20px;
      height: 20px;
    }

    &:hover {
      color: $navy;
      .micon {
        fill: $navy;
      }
    }
  }

  &__section-title {
    font-size: 20px;
    font-weight: 700;
    color: $navy;
    margin-bottom: 20px;
  }

  &__content {
    background: #fff;
    border: 1px solid $border-color;
    border-radius: 12px;
    padding: 20px;
  }
}

.settings-group {
  margin-bottom: 20px;

  & + & {
    margin-top: 24px;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    margin-bottom: 12px;

    &::before {
      content: "";
      width: 3px;
      height: 16px;
      background: $teal;
      border-radius: 2px;
      flex-shrink: 0;
    }
  }

  &__body {
    background: $header-bg;
    border-radius: 12px;
    padding: 16px;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 900px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }
}

.settings-card {
  background: #fff;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  color: inherit;

  &:hover {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.07);
    transform: translateY(-1px);
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .micon {
      width: 18px;
      height: 18px;
    }

    &--navy {
      background: rgba(0, 45, 94, 0.08);
      .micon {
        fill: $navy;
      }
    }
    &--teal {
      background: $teal-light;
      .micon {
        fill: #009b7d;
      }
    }
    &--purple {
      background: rgba(109, 40, 217, 0.08);
      .micon {
        fill: #6d28d9;
      }
    }
    &--warning {
      background: rgba(245, 158, 11, 0.08);
      .micon {
        fill: #b45309;
      }
    }
    &--green {
      background: rgba(34, 197, 94, 0.08);
      .micon {
        fill: #16a34a;
      }
    }
    &--danger {
      background: rgba(239, 68, 68, 0.08);
      .micon {
        fill: #dc2626;
      }
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__label {
    font-size: 13.5px;
    font-weight: 600;
    color: $text-primary-new;
    margin-bottom: 1px;
  }

  &__desc {
    font-size: 11.5px;
    color: $text-secondary-new;
    line-height: 1.4;
  }
}
</style>
